import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getProductById, getStoreById, getProductsByStore } from '../lib/firebaseService';
import { ArrowLeft, ShoppingCart, Package, Info, Menu, X, Store as StoreIcon, MessageCircle, Plus, Minus } from 'lucide-react';

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [otherProducts, setOtherProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [cartItemCount, setCartItemCount] = useState(0);

  // Load cart count from localStorage
  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cartData = localStorage.getItem('cart');
        if (cartData) {
          const cart = JSON.parse(cartData);
          const count = cart.reduce((sum, item) => sum + item.quantity, 0);
          setCartItemCount(count);
        }
      } catch (error) {
        console.error('Cart count error:', error);
      }
    };
    
    updateCartCount();
    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    return () => window.removeEventListener('storage', updateCartCount);
  }, []);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const productData = await getProductById(productId);
      if (productData) {
        setProduct(productData);
        // Set first image if available
        if (productData.image_urls && productData.image_urls.length > 0) {
          setSelectedImage(0);
        }
        
        // Load store data
        const storeData = await getStoreById(productData.store_id);
        setStore(storeData);
        
        // Load other products from the same store
        const allProducts = await getProductsByStore(productData.store_id);
        const filteredProducts = allProducts.filter(p => p.id !== productId);
        setOtherProducts(filteredProducts);
      } else {
        toast.error('Məhsul tapılmadı');
        navigate(-1);
      }
    } catch (error) {
      console.error('Məhsul yüklənə bilmədi:', error);
      toast.error('Məhsul yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  // Add to cart function with localStorage
  const addToCart = () => {
    if (product.stock <= 0) {
      toast.error('Məhsul stokda yoxdur');
      return;
    }

    try {
      // Get current cart from localStorage
      const cartData = localStorage.getItem('cart');
      let cart = cartData ? JSON.parse(cartData) : [];

      // Check if product already in cart
      const existingIndex = cart.findIndex(item => item.id === product.id);

      if (existingIndex !== -1) {
        // Update quantity
        const newQuantity = cart[existingIndex].quantity + quantity;
        if (newQuantity > product.stock) {
          toast.error(`Maksimum ${product.stock} ədəd əlavə edə bilərsiniz`);
          return;
        }
        cart[existingIndex].quantity = newQuantity;
      } else {
        // Add new item
        cart.push({
          id: product.id,
          name: product.name,
          price: product.price,
          image_url: product.image_url || product.image_urls?.[0],
          quantity: quantity,
          stock: product.stock,
          store_id: product.store_id
        });
      }

      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(cart));
      toast.success(`${quantity} ədəd səbətə əlavə edildi`);
      
      // Update cart count
      const count = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartItemCount(count);
      
      // Reset quantity to 1
      setQuantity(1);
    } catch (error) {
      console.error('Səbətə əlavə edilə bilmədi:', error);
      toast.error('Səbətə əlavə edilə bilmədi');
    }
  };

  const increaseQuantity = () => {
    if (quantity < product.stock) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Yüklənir...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">Məhsul tapılmadı</p>
      </div>
    );
  }

  const images = product.image_urls || [product.image_url];
  const currentImage = images[selectedImage] || product.image_url;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Store Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          {/* Sol - Geri düyməsi */}
          <button
            onClick={() => navigate(`/store/${store?.id || ''}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-to-store-btn"
          >
            <ArrowLeft className="w-7 h-7 text-gray-900" />
          </button>

          {/* Orta - Logo və Mağaza adı */}
          <Link 
            to={`/store/${store?.id || ''}`}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="store-header-link"
          >
            {store?.logo_url ? (
              <img src={store.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <StoreIcon className="w-5 h-5 text-gray-500" />
              </div>
            )}
            <span className="font-semibold text-lg text-gray-900 truncate max-w-[180px]">
              {store?.store_name || 'Mağaza'}
            </span>
          </Link>

          {/* Sağ - Səbət */}
          <button
            onClick={() => navigate(`/store/${store?.id || ''}`)}
            className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="cart-btn"
          >
            <ShoppingCart className="w-7 h-7 text-gray-900" />
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 bg-black text-white text-sm rounded-full flex items-center justify-center font-bold">
                {cartItemCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 p-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={currentImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x600?text=Şəkil+Yoxdur';
                  }}
                />
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-black' : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-black mb-4">{product.name}</h1>
                
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-4xl font-bold text-black">
                    {product.price} AZN
                  </span>
                  {product.stock > 0 ? (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                      Stokda var
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                      Stokda yoxdur
                    </span>
                  )}
                </div>

                {product.description && (
                  <div className="mb-6">
                    <h3 className="flex items-center gap-2 text-lg font-semibold mb-2">
                      <Info className="w-5 h-5" />
                      Məhsul Haqqında
                    </h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {product.description}
                    </p>
                  </div>
                )}

                {product.category && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-500">
                      <strong>Kateqoriya:</strong> {product.category}
                    </p>
                  </div>
                )}

                {product.stock > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <strong>Mövcud:</strong> {product.stock} ədəd
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-6 space-y-4">
                {/* Quantity Selector */}
                {product.stock > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Miqdar</label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={decreaseQuantity}
                        disabled={quantity <= 1}
                        className="w-10 h-10 p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-lg font-semibold w-12 text-center">
                        {quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={increaseQuantity}
                        disabled={quantity >= product.stock}
                        className="w-10 h-10 p-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-500 ml-2">
                        (Max: {product.stock})
                      </span>
                    </div>
                  </div>
                )}

                {/* Add to Cart Button */}
                {product.stock > 0 ? (
                  <Button
                    onClick={addToCart}
                    className="w-full h-12 bg-black hover:bg-gray-800 text-white text-base font-semibold"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Səbətə əlavə et
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="w-full h-12 bg-gray-300 text-gray-500 cursor-not-allowed"
                  >
                    Stokda yoxdur
                  </Button>
                )}

                {/* Continue Shopping */}
                <Button
                  variant="outline"
                  onClick={() => navigate(`/store/${store?.id || ''}`)}
                  className="w-full h-12"
                  data-testid="back-to-store-btn-bottom"
                >
                  Alış-verişə davam et
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Other Products from the same store */}
        {otherProducts.length > 0 && (
          <div className="mt-8 max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-black mb-6">
              Bu mağazanın digər məhsulları
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {otherProducts.slice(0, 8).map((otherProduct) => (
                <Link
                  key={otherProduct.id}
                  to={`/product/${otherProduct.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-all cursor-pointer block"
                  data-testid={`other-product-${otherProduct.id}`}
                >
                  <div className="aspect-square relative">
                    {otherProduct.image_url || otherProduct.media?.[0]?.url ? (
                      <img
                        src={otherProduct.media?.[0]?.url || otherProduct.image_url}
                        alt={otherProduct.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    {otherProduct.stock <= 0 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-medium text-sm">Stokda yoxdur</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
                      {otherProduct.name}
                    </h3>
                    <span className="font-bold text-black">{otherProduct.price} ₼</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
